import { useEffect } from "react";
import { LanguagesType } from "../../models/languagesType";
import { displayLinks, DisplayLinksType } from "../../utils/displayLinks";
import { WAProduct } from "../../utils/productType";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./Footer.module.scss";
import { FooterList } from "./FooterList";

export type FooterProps = {
  lang: LanguagesType;
  productName?: WAProduct;
};

export const Footer: React.FC<FooterProps> = ({ lang, productName }) => {
  const { t, setLanguage } = useTranslation();

  const displayedLinks: DisplayLinksType = displayLinks[lang as LanguagesType];

  const analysisParam: string = "?utm_medium=referral&utm_source=wa-snow&utm_campaign=footer";

  useEffect(() => {
    setLanguage(lang);
  }, [lang]);

  return (
    <footer
      className={`${styles.footer} ${styles[`${lang}`]} ${
        productName === "activity" ? styles.activity : ""
      }`}
    >
      <div className={styles.footerListContainer}>
        <div>
          <h2 className={styles.footerListTitle}>
            {t("footer.about_wamazing")}
          </h2>
          <ul className={styles.footerList}>
            <FooterList
              href={t("links.corporate")}
              title={t("base.company_info")}
            />
            <FooterList
              href="https://www.wamazing.jp/terms/user"
              title={t("base.terms_of_use")}
            />
            <FooterList
              href="https://www.wamazing.jp/terms/privacy"
              title={t("base.privacy_policy")}
            />
            <FooterList href={t("links.faq")} title={t("base.faq")} />
            <FooterList
              href={t("links.contact")}
              title={t("base.contact_us")}
            />
            <FooterList
              href={t("links.transcription")}
              title={t("base.transcription")}
            />
          </ul>
        </div>

        <div>
          <h2 className={styles.footerListTitle}>
            {t("footer.wamazing_services")}
          </h2>
          <ul className={styles.footerList}>
            {displayedLinks.yado && (
              <FooterList
                href={t("links.yado") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.stay")}
              />
            )}
            {displayedLinks.activity && (
              <FooterList
                href={t("links.activity") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.play")}
              />
            )}
            {displayedLinks.snow && (
              <FooterList
                href={t("links.snow") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.snow")}
              />
            )}
            {displayedLinks.ticket && (
              <FooterList
                href={t("links.ticket") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.transport")}
              />
            )}
            {displayedLinks.kaimono && (
              <FooterList
                href={t("links.kaimono") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.shopping")}
              />
            )}
            {displayedLinks.discover && (
              <FooterList
                href={t("links.discover") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.discover")}
              />
            )}

            {/* 台湾当局から違法性の指摘があったため、全リージョンで一時的に保険関連を非表示にする対応
            {displayedLinks.insurance && (
              <FooterList
                href={t("links.insurance") + analysisParam}
                title={t("base.wamazing")}
                productName={t("product.insurance")}
              />
            )} */}
          </ul>
        </div>

        <div>
          <h2 className={styles.footerListTitle}>{t("footer.app")}</h2>
          <ul className={styles.footerList}>
            {(lang === "zh_tw" || lang === "zh_hk") && (
              <FooterList 
                href={lang === "zh_tw" 
                  ? "https://tw.wamazing.com/top/app-manual/?utm_source=wa-snow&utm_medium=referral&utm_campaign=footer" 
                  : "https://hk.wamazing.com/top/app-manual/?utm_source=wa-snow&utm_medium=referral&utm_campaign=footer"} 
                title={t("footer.japan_travel_app")} 
              />
            )}
            {(lang === "zh_tw" || lang === "zh_hk") && displayedLinks.sim && (
              <FooterList 
                href={lang === "zh_tw" 
                  ? "https://tw.wamazing.com/top/sim-manual/?utm_source=wa-snow&utm_medium=referral&utm_campaign=footer" 
                  : "https://hk.wamazing.com/top/sim-manual/?utm_source=wa-snow&utm_medium=referral&utm_campaign=footer"} 
                title={t("base.wamazing")} 
                productName={t("product.sim")} 
              />
            )}
            <FooterList href="https://app.adjust.com/oq033b2" title="iPhone" />
            <FooterList href="https://app.adjust.com/oq033b2" title="Android" />
          </ul>
        </div>
        <div>
          {(t("links.facebook") !== "" || t("links.instagram") !== "") && (
            <h2 className={styles.footerListTitle}>{t("footer.sns")}</h2>
          )}
          <ul className={`${styles.footerList} ${styles.sns}`}>
            {t("links.instagram") && (
              <FooterList href={t("links.instagram")}>
                <span className={`icon-instagram`}></span>
              </FooterList>
            )}
            {t("links.facebook") && (
              <FooterList href={t("links.facebook")}>
                <span className={`icon-facebook`}></span>
              </FooterList>
            )}
            {t("links.whatsapp") && (
              <FooterList href={t("links.whatsapp")}>
                <span className={`icon-whatsapp`}></span>
              </FooterList>
            )}
          </ul>
        </div>
      </div>
      <div className={styles.copyright}>
        <div>{t("footer.travel_agency_registration")}</div>
        <span className={styles.small}>
          © WAmazing inc., All Rights Reserved.
        </span>
      </div>
    </footer>
  );
};
